import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Sparkles } from "lucide-react";
import {
  getMaintenanceSystemPromptPreview,
  MaintenanceAgentFormData,
} from "../../../utils/maintenanceSystemPrompt";
import type { ToolType } from "../../../types";

type Step2MaintenanceConfigurationProps = {
  agentFormData: MaintenanceAgentFormData;
  onFormDataChange: (data: MaintenanceAgentFormData) => void;
  displayName: string;
};

const ALL_TOOLS = [
  { value: "transferConversation", label: "Transferir Conversacion" },
  { value: "scheduleVisit", label: "Agendar Visita" },
  { value: "getAvailableSlots", label: "Obtener horarios disponibles" },
  { value: "getProperty", label: "Obtener Propiedad" },
  { value: "getPropertyByCode", label: "Obtener Propiedad por Codigo" },
  { value: "searchProperties", label: "Buscar Propiedades" },
  { value: "assignAgent", label: "Asignar Agente" },
  { value: "setEmail", label: "Correo electrónico" },
  { value: "registerListingRequest", label: "Registrar Solicitud de Publicacion" },
  { value: "discardLead", label: "Descartar Lead" },
  { value: "calculateDaysFor", label: "Calcular Dias" },
  { value: "createMaintenanceRequest", label: "Registrar Solicitud de Mantenimiento" },
  { value: "getCustomerProperties", label: "Obtener inmuebles del cliente" },
  { value: "getCustomerDocuments", label: "Obtener documentos del cliente" },
  { value: "createServiceRequest", label: "Crear solicitud de servicio" },
  { value: "updateServiceRequest", label: "Actualizar solicitud de servicio" },
  { value: "updatePipelinePosition", label: "Actualizar estado del usuario" },
  { value: "lookupByContractNumber", label: "Buscar por Numero de Contrato" },
  { value: "setAppointmentIntention", label: "Registrar intencion de cita" },
  { value: "sendMedia", label: "Enviar multimedia" },
] as const;

const Step2MaintenanceConfiguration = ({
  agentFormData,
  onFormDataChange,
  displayName,
}: Step2MaintenanceConfigurationProps) => {
  return (
    <div className="flex flex-col gap-6">
      <p className="text-lg font-semibold mb-4">
        Configuracion del Agente de Mantenimientos
      </p>
      {/* Split layout with form on left and prompt preview on right */}
      <div className="flex gap-6 items-start">
        {/* Left side - Form */}
        <div className="flex-1">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">
                Nombre del agente <span className="text-red-500">*</span>
              </Label>
              <Input
                value={agentFormData.agentName}
                onChange={(e) => onFormDataChange({ ...agentFormData, agentName: e.target.value })}
                placeholder="Ingresa el nombre del agente"
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">
                Enlace a la politica de tratamiento de datos <span className="text-red-500">*</span>
              </Label>
              <Input
                value={agentFormData.politicaTratamientoDeDatos}
                onChange={(e) =>
                  onFormDataChange({
                    ...agentFormData,
                    politicaTratamientoDeDatos: e.target.value,
                  })
                }
                placeholder="https://ejemplo.com/politica-de-datos"
                type="url"
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">
                Pagina web de la inmobiliaria <span className="text-red-500">*</span>
              </Label>
              <Input
                value={agentFormData.webPage}
                onChange={(e) => onFormDataChange({ ...agentFormData, webPage: e.target.value })}
                placeholder="https://ejemplo.com"
                type="url"
              />
            </div>
          </div>
        </div>

        {/* Right side - Prompt Preview and Tools */}
        <div className="flex-1">
          <div className="flex flex-col gap-6">
            {/* Prompt Preview */}
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <Sparkles className="size-5 text-purple-500" />
                <p className="text-base font-semibold">
                  Vista previa de las instrucciones
                </p>
              </div>
              <Textarea
                value={getMaintenanceSystemPromptPreview({
                  agentFormData,
                  displayName,
                })}
                readOnly
                className="h-[121vh] resize-y bg-gray-50 border border-gray-200 text-sm font-mono p-4 overflow-y-auto"
              />
            </div>

            {/* Tools Selection */}
            <div>
              <p className="text-base font-semibold mb-4">
                Herramientas
              </p>
              <div className="flex flex-col gap-2">
                {ALL_TOOLS.map((tool) => (
                  <div key={tool.value} className="flex items-center gap-2">
                    <Checkbox
                      checked={
                        agentFormData.selectedTools?.includes(tool.value as ToolType) ?? false
                      }
                      onCheckedChange={(checked) => {
                        const currentTools = agentFormData.selectedTools || [];
                        const toolValue = tool.value as ToolType;
                        const newTools = checked
                          ? [...currentTools, toolValue]
                          : currentTools.filter((t) => t !== toolValue);
                        onFormDataChange({
                          ...agentFormData,
                          selectedTools: newTools,
                        });
                      }}
                    />
                    <label className="text-sm">{tool.label}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step2MaintenanceConfiguration;
